import React, { useEffect, useRef, useState } from 'react';
import { Textarea } from './ui/textarea';
import api from '../utils/api';

/**
 * Textarea with @-mention autocomplete for role codes.
 * Roles are fetched from GET /mentions/roles.
 */
const MentionTextarea = ({ value, onChange, placeholder, rows = 3, testId, disabled }) => {
  const [roles, setRoles] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  const textareaRef = useRef(null);
  const anchorPosRef = useRef(-1); // index of the "@" in value

  useEffect(() => {
    let cancelled = false;
    api.get('/mentions/roles').then((r) => {
      if (!cancelled) setRoles(r.data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const filtered = roles.filter(
    (r) => r.code.toLowerCase().startsWith(filter.toLowerCase())
        || r.label.toLowerCase().includes(filter.toLowerCase())
  ).slice(0, 6);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const caret = e.target.selectionStart;
    // Find last '@' before caret with no whitespace in-between
    const beforeCaret = v.slice(0, caret);
    const atIdx = beforeCaret.lastIndexOf('@');
    if (atIdx >= 0) {
      const chunk = beforeCaret.slice(atIdx + 1);
      if (!/\s/.test(chunk) && chunk.length <= 20) {
        anchorPosRef.current = atIdx;
        setFilter(chunk);
        setOpenDropdown(true);
        setHighlight(0);
        return;
      }
    }
    setOpenDropdown(false);
    anchorPosRef.current = -1;
  };

  const insertMention = (code) => {
    const atIdx = anchorPosRef.current;
    if (atIdx < 0) return;
    const before = value.slice(0, atIdx);
    const after = value.slice(atIdx).replace(/^@[a-zA-Z0-9_]*/, `@${code} `);
    const newVal = before + after;
    onChange(newVal);
    setOpenDropdown(false);
    anchorPosRef.current = -1;
    // Restore focus
    setTimeout(() => {
      textareaRef.current?.focus();
      const pos = (before + `@${code} `).length;
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!openDropdown || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(filtered[highlight].code);
    } else if (e.key === 'Escape') {
      setOpenDropdown(false);
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        data-testid={testId}
      />
      {openDropdown && filtered.length > 0 && (
        <div
          className="absolute z-30 mt-1 w-64 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg overflow-hidden"
          data-testid="mention-dropdown"
        >
          <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
            Ping role
          </div>
          {filtered.map((r, i) => (
            <button
              key={r.code}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(r.code); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 text-sm ${
                i === highlight ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60'
              }`}
              data-testid={`mention-option-${r.code}`}
            >
              <span className="font-medium">@{r.code}</span>
              <span className="text-xs text-muted-foreground truncate">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
